import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { service } from '@ember/service';
import { t } from 'ember-intl';
import type { IntlService } from 'ember-intl';
import { formatCurrency } from 'tio-ui/format';
import Button from 'tio-ui/components/button';
import { PhCreditCard, PhUploadSimple } from 'ember-phosphor-icons';
import type { ComponentLike } from '@glint/template';
import type { IconBadgeColor } from 'tio-ui/components/icon-badge';
import { getSingleValueForTasField } from 'tio-common/utils/tuition-assistance/fields';
import { courseCashMark } from 'tio-common/utils/course-cash-mark';
import { PLACEHOLDER_MERCHANT_NAME } from 'tio-common/utils/course-cash/transaction-adapter';
import type { CourseCashTransaction } from 'tio-common/types/course-cash-transaction';
import type { SafeString } from '@ember/template';
import TaskCard from './task-card';
import type { TaskCardChipIntent } from './task-card';
import AnchorMessage from './anchor-message';
import CourseCashBody from './task-card/course-cash';
import type { CourseCashSwipeRow } from './task-card/course-cash';
import CourseDocumentsBody from './task-card/course-documents';
import SubmitEvidenceBody from './task-card/submit-evidence';
import { summarizeRemaining } from 'tio-employee/utils/tas/evidence-checklist';
import type { EvidenceChecklist } from 'tio-employee/utils/tas/evidence-checklist';
import type { SubmitEvidenceBundle } from './task-card/submit-evidence';
import type {
  AttentionModel,
  AttentionTask,
  AttentionTaskKind,
  AttentionTaskUrgency,
  AttentionAnchorKind,
} from 'tio-employee/utils/tas/attention-tasks';
import type { TasApplicationExtension as TasApplicationModel } from 'tio-data/data/resources/tas-application.ext';

const noop = () => {};

/** MM/DD/YYYY from a YYYY-MM-DD(THH:..) string, read as calendar parts (see
 * chipFor's note on UTC-parse off-by-one days). Undefined when malformed. */
function fullDateLabel(value: string | null | undefined): string | undefined {
  if (!value) {
    return undefined;
  }
  const datePart = value.split('T')[0] ?? '';
  const [year, month, day] = datePart.split('-');
  if (!year || !month || !day) {
    return undefined;
  }
  return `${month}/${day}/${year}`;
}

type TaskIcon = ComponentLike<{ Args: { size: number }; Element: SVGElement }>;

const TASK_ICONS: Record<AttentionTaskKind, TaskIcon> = {
  'use-course-cash': PhCreditCard,
  'upload-receipt': PhUploadSimple,
  'submit-evidence': PhUploadSimple,
  'upload-course-documents': PhUploadSimple,
};

const TASK_COLORS: Record<AttentionTaskKind, IconBadgeColor> = {
  'use-course-cash': 'purple',
  'upload-receipt': 'teal',
  'submit-evidence': 'teal',
  'upload-course-documents': 'teal',
};

// Deadline-driven urgency treatment (Figma: Task: Provide Your CourseCa$h™
// Receipt spec — Default/green, Warning/yellow, Error/red). Only tasks that
// set `urgency` (currently upload-receipt) override the kind-based defaults.
const URGENCY_ICON_COLORS: Record<AttentionTaskUrgency, IconBadgeColor> = {
  default: 'green',
  warning: 'amber',
  error: 'red',
};

const URGENCY_CHIP_INTENTS: Record<AttentionTaskUrgency, TaskCardChipIntent> = {
  default: 'success',
  warning: 'warning',
  error: 'danger',
};

const TASK_TITLE_KEYS: Record<AttentionTaskKind, string> = {
  'use-course-cash': 'tuition_assistance.applications.manage.tasks.use_course_cash_title',
  'upload-receipt': 'tuition_assistance.applications.manage.tasks.upload_receipt_title',
  'submit-evidence': 'tuition_assistance.applications.manage.tasks.submit_evidence_title',
  'upload-course-documents':
    'tuition_assistance.applications.manage.tasks.upload_course_documents_title',
};

// Task kinds whose title embeds the CourseCa$h™ brand mark and needs styling.
const COURSE_CASH_TASK_KINDS = new Set<AttentionTaskKind>(['use-course-cash', 'upload-receipt']);

interface TaskRow {
  task: AttentionTask;
  icon: TaskIcon;
  color: IconBadgeColor;
  chipIntent: TaskCardChipIntent;
  title: string | SafeString;
  /** Resolved from the panel's per-task-id open state, not from the card. */
  isOpen: boolean;
  chip?: string;
  /** Always-visible subtitle under the title/chip row — only submit-evidence
   * sets this today (Figma's Default/Warning/Error copy). */
  description?: string;
  /** MM/DD/YYYY label for the swipe this receipt task is for, when known — lets
   * concurrent upload-receipt cards read as distinct instead of identical. */
  transactionDateLabel?: string;
  /** Formatted dollar amount for the upload-receipt detail row (Figma: date +
   * school + amount, matching the CourseCash receipt banner). */
  amountLabel?: string;
  /** Who the swipe was charged to, for the upload-receipt detail row — the
   * card-network merchant when the network named one, else the enrolled
   * institution (see merchantLabelFor). */
  merchantLabel?: string;
  /** MM/DD/YYYY for the use-course-cash task's "Use By" detail row (Figma:
   * "Task: Use Your CourseCa$h™" spec) — same underlying date as the task's
   * chip, just spelled out in full for CourseCashBody's own detail row. */
  useByDateLabel?: string;
  /** Countable requirements behind this task; only submit-evidence has them. */
  progressDone?: number;
  progressTotal?: number;
  /** Outstanding requirement names as prose, empty once none remain. */
  remainingSummary?: string;
}

interface AnchorRow {
  kind: AttentionAnchorKind;
  title: string | SafeString;
  body?: string | SafeString;
  variant: 'check' | 'glyph' | 'alert';
}

/** Just-for-fun confetti-burst treatment on the approved anchor's congratulations
 * card. Piece count only — per-piece color/direction/delay are pure CSS
 * (`:nth-child` rules in `app/styles/reimagined/tas.css`), so no inline styles. */
const CONFETTI_PIECE_COUNT = 20;
const CONFETTI_PIECES = Array.from({ length: CONFETTI_PIECE_COUNT });

interface NeedsYourAttentionSignature {
  Args: {
    model: AttentionModel;
    application: TasApplicationModel;
    onViewActivity?: () => void;
    onSubmitEvidence?: SubmitEvidenceBundle;
    onUploadReceipt?: (task: AttentionTask) => void;
    /** Authorized swipes awaiting capture, rendered as informational rows. */
    pendingCaptureTransactions?: CourseCashTransaction[];
    /** Uploaded/verified receipt rows whose View button calls onViewReceipt. */
    resolvedReceiptTransactions?: CourseCashTransaction[];
    onViewReceipt?: (transaction: CourseCashTransaction) => void;
  };
  Element: HTMLElement;
}

/**
 * The "Needs Your Attention" panel for the TAS course-management screen. Renders
 * the derived `AttentionModel` (see `utils/tas/attention-tasks`): informational
 * anchors first, then the non-completed action tasks as expandable cards. Renders
 * nothing when the model is not visible.
 */
export default class NeedsYourAttention extends Component<NeedsYourAttentionSignature> {
  @service declare intl: IntlService;

  get countLabel(): string {
    const { model } = this.args;
    if (model.pendingCount > 0) {
      return this.intl.t('tuition_assistance.applications.manage.tasks_pending', {
        count: model.pendingCount,
      });
    }
    // The all-caught-up empty state shows "(0)"; the submitted anchor shows no suffix.
    if (model.anchors.some((a) => a.kind === 'all-caught-up')) {
      return this.intl.t('tuition_assistance.applications.manage.empty_count', { count: 0 });
    }
    return '';
  }

  /**
   * Cents → a locale-formatted currency string. Only for amounts interpolated
   * into translated copy; the plain en-US detail rows elsewhere in this file
   * keep tio-ui's `formatCurrency`, which is deliberately locale-pinned.
   */
  formatAmount = (cents: number): string => {
    return this.intl.formatNumber(cents / 100, {
      style: 'currency',
      currency: 'USD',
    });
  };

  get anchorRows(): AnchorRow[] {
    const ns = 'tuition_assistance.applications.manage.anchors';
    return this.args.model.anchors.map((anchor) => {
      const kind: AttentionAnchorKind = anchor.kind;
      if (kind === 'approved') {
        return {
          kind,
          title: this.intl.t(`${ns}.approved_title`),
          body: this.intl.t(`${ns}.approved_body`),
          variant: 'check',
        };
      }
      if (kind === 'course-cash-used') {
        return {
          kind,
          title: courseCashMark(this.intl.t(`${ns}.course_cash_used_title`)),
          body: courseCashMark(this.intl.t(`${ns}.course_cash_used_body`)),
          variant: 'check',
        };
      }
      if (kind === 'all-caught-up') {
        return {
          kind,
          title: this.intl.t(`${ns}.caught_up_title`),
          body: this.intl.t(`${ns}.caught_up_body`),
          variant: 'glyph',
        };
      }
      if (kind === 'evidence-submitted') {
        return {
          kind,
          title: this.intl.t(`${ns}.evidence_submitted_title`),
          body: this.intl.t(`${ns}.evidence_submitted_body`),
          variant: 'check',
        };
      }
      if (kind === 'in-repayment') {
        return {
          kind,
          title: this.intl.t(`${ns}.in_repayment_title`),
          body: this.intl.t(`${ns}.in_repayment_body`, {
            // Not tio-ui's formatCurrency: that pins the locale to en-US, which
            // would render "$7.43" inside the es-mx / fr-ca copy. The service
            // formats against the active locale and re-renders when it changes.
            amount: this.formatAmount(anchor.outstandingRepayment ?? 0),
          }),
          variant: 'alert',
        };
      }
      if (kind === 'abandoned') {
        return {
          kind,
          title: this.intl.t(`${ns}.abandoned_title`),
          body: this.intl.t(`${ns}.abandoned_body`),
          variant: 'alert',
        };
      }
      return {
        kind,
        title: this.intl.t(`${ns}.submitted_title`),
        body: this.intl.t(`${ns}.submitted_body`),
        variant: 'check',
      };
    });
  }

  /**
   * Expanded/collapsed per card, keyed by `keyFor`.
   *
   * Held here rather than in TaskCard because a card is destroyed and rebuilt
   * whenever its task's completion flips — the active list and the completed
   * drawers are separate render sites — and the key is what survives that move.
   * A key absent from this map has never been clicked and falls back to
   * `seedOpenFor`; once clicked, the remembered value wins for the rest of the
   * visit, so nothing but a click can change it.
   */
  @tracked private openCards: Record<string, boolean> = {};

  /**
   * Whether a card starts expanded, for a task the employee has not yet touched.
   * A seed only ever applies before the first click on that card.
   */
  seedOpenFor = (task: AttentionTask): boolean => {
    if (task.kind !== 'use-course-cash') {
      return false;
    }

    // An unspent card leads with its balance. A partially-spent one arrives
    // already 'completed', so it opens only while there is still money to swipe.
    return task.status === 'completed' ? this.completedCourseCashOpen : true;
  };

  /**
   * Task ids are literals (`submit-evidence`, `use-course-cash`) reused by every
   * application, and navigating between two manage screens swaps this
   * component's model without remounting it — so the application has to be part
   * of the key or one application's choice would seed another's.
   */
  private keyFor(task: AttentionTask): string {
    return `${this.args.application.id}:${task.id}`;
  }

  isCardOpen = (task: AttentionTask | undefined): boolean => {
    if (!task) {
      return false;
    }

    return this.openCards[this.keyFor(task)] ?? this.seedOpenFor(task);
  };

  /** The only path that changes open state. */
  toggleCard = (task: AttentionTask | undefined) => {
    if (!task) {
      return;
    }

    this.openCards = { ...this.openCards, [this.keyFor(task)]: !this.isCardOpen(task) };
  };

  /** Reads through isCardOpen so a click is remembered across the card's move. */
  get completedCourseCashIsOpen(): boolean {
    return this.isCardOpen(this.completedCourseCashTask);
  }

  get completedCourseDocumentsIsOpen(): boolean {
    return this.isCardOpen(this.completedCourseDocumentsTask);
  }

  get taskRows(): TaskRow[] {
    return this.args.model.tasks
      .filter((task) => task.status !== 'completed')
      .map((task) => ({
        task,
        isOpen: this.isCardOpen(task),
        icon: TASK_ICONS[task.kind],
        color: task.urgency ? URGENCY_ICON_COLORS[task.urgency] : TASK_COLORS[task.kind],
        chipIntent: task.urgency ? URGENCY_CHIP_INTENTS[task.urgency] : 'info',
        title: this.titleFor(task),
        chip: this.chipFor(task),
        description: this.descriptionFor(task),
        transactionDateLabel: this.transactionDateLabelFor(task),
        amountLabel: this.amountLabelFor(task),
        merchantLabel: this.merchantLabelFor(task),
        useByDateLabel: this.useByDateLabelFor(task),
        progressDone: this.progressFor(task)?.doneCount,
        progressTotal: this.progressFor(task)?.totalCount,
        remainingSummary: this.remainingSummaryFor(task),
      }));
  }

  /**
   * Only the evidence task carries countable requirements today. Other kinds
   * return undefined so their headers stay unchanged — a meter reading "0 of 0"
   * on a task with nothing to count would be worse than no meter.
   *
   * Gated on the same state as `SubmitEvidenceActions#isSubmittable`: the card
   * deliberately renders in the review and settled states, and `hasAgreedTerms`
   * reads false there because the accepted conditions are session-local and
   * start empty on every load. Unguarded, a settled card counts an outstanding
   * requirement against work its owner already submitted.
   */
  progressFor = (task: AttentionTask): EvidenceChecklist | undefined => {
    if (task.kind !== 'submit-evidence') {
      return undefined;
    }
    if (this.args.application.state !== 'TAS.ApplicationState.ATTEND') {
      return undefined;
    }
    return this.args.onSubmitEvidence?.checklist;
  };

  remainingSummaryFor = (task: AttentionTask): string | undefined => {
    const checklist = this.progressFor(task);
    if (!checklist) {
      return undefined;
    }
    return summarizeRemaining(checklist.remaining, this.intl.primaryLocale) || undefined;
  };

  /** Shared with titleFor's amount-qualified title — one place computing the
   * cents→dollars conversion for a task's amount. */
  amountLabelFor = (task: AttentionTask): string | undefined => {
    return task.amount != null ? formatCurrency(task.amount / 100) : undefined;
  };

  get isEvidenceUnderReview(): boolean {
    return this.args.application.state === 'TAS.ApplicationState.PENDING_EVIDENCE_APPROVAL';
  }

  /**
   * Concurrent upload-receipt tasks (one per swipe) otherwise render as
   * identical cards — fold the swipe amount into the title so they're
   * distinguishable even collapsed. Other kinds, and the single-task fallback
   * with no live amount data, use the plain kind-based title. CourseCash-brand
   * kinds also get the trademark-mark treatment (matches completedCourseCashTitle
   * and the course-cash-used anchor above).
   */
  titleFor = (task: AttentionTask): string | SafeString => {
    const amountLabel = this.amountLabelFor(task);
    const raw =
      task.kind === 'upload-receipt' && amountLabel
        ? this.intl.t(
            'tuition_assistance.applications.manage.tasks.upload_receipt_title_for_amount',
            { amount: amountLabel }
          )
        : task.kind === 'submit-evidence' && this.isEvidenceUnderReview
          ? this.intl.t('tuition_assistance.applications.manage.tasks.evidence_under_review_title')
          : this.intl.t(TASK_TITLE_KEYS[task.kind]);
    return COURSE_CASH_TASK_KINDS.has(task.kind) ? courseCashMark(raw) : raw;
  };

  /**
   * Where the card was used, for the upload-receipt detail row. Prefers the
   * card-network merchant the server resolved off the swipe's authorization;
   * falls back to the enrolled institution, which is who a tuition charge
   * usually — but not always — lands with. Empty when neither is known, which
   * leaves the row as date + amount rather than printing a placeholder.
   */
  merchantLabelFor = (task: AttentionTask): string | undefined => {
    if (task.kind !== 'upload-receipt') {
      return undefined;
    }
    return task.merchantName || this.institutionName || undefined;
  };

  /**
   * Always-visible subtitle under submit-evidence's title/chip row (Figma
   * "Task Group: Evidence Submission" spec — distinct copy per Default/
   * Warning/Error urgency). `task.urgency` is `undefined` for Default (see
   * attention-tasks.ts's deriveAttention — that's deliberate, so the icon
   * stays plain teal instead of picking up upload-receipt's green), so
   * `undefined` selects the same Default copy here. Other task kinds don't
   * have this treatment yet, so they get no description.
   */
  descriptionFor = (task: AttentionTask): string | undefined => {
    if (task.kind !== 'submit-evidence' || this.isEvidenceUnderReview) {
      return undefined;
    }
    const ns = 'tuition_assistance.applications.manage.tasks';
    if (task.urgency === 'warning') {
      return this.intl.t(`${ns}.submit_evidence_body_warning`);
    }
    if (task.urgency === 'error') {
      return this.intl.t(`${ns}.submit_evidence_body_error`);
    }
    return this.intl.t(`${ns}.submit_evidence_body`);
  };

  /** Full MM/DD/YYYY of the swipe — not the chip's compact MM/DD, since the
   * body has room for the full date. */
  transactionDateLabelFor = (task: AttentionTask): string | undefined => {
    return fullDateLabel(task.transactionDate);
  };

  /** MM/DD/YYYY from the use-course-cash task's raw dueDate, for
   * CourseCashBody's "Use By" detail row (Figma: "Task: Use Your
   * CourseCa$h™" spec) — same underlying date `chipFor` already renders as
   * the compact chip, just spelled out for the detail row's own room. */
  useByDateLabelFor = (task: AttentionTask): string | undefined => {
    return fullDateLabel(task.dueDate);
  };

  /** Transaction-level version of merchantLabelFor above: the adapter
   * coalesces an unknown merchant to a generic placeholder, and these rows
   * would rather show the school (or nothing) than the word "Merchant". */
  merchantLabelForTransaction = (transaction: CourseCashTransaction): string | undefined => {
    const merchantName =
      transaction.merchantName === PLACEHOLDER_MERCHANT_NAME ? '' : transaction.merchantName;
    return merchantName || this.institutionName || undefined;
  };

  /** Formats authorized swipes with a Processing label for CourseCashBody. */
  get pendingCaptureRows(): CourseCashSwipeRow[] {
    return (this.args.pendingCaptureTransactions ?? []).map((transaction) => ({
      transaction,
      amountLabel: formatCurrency(transaction.amount / 100),
      dateLabel: fullDateLabel(transaction.transactionDate),
      merchantLabel: this.merchantLabelForTransaction(transaction),
      statusLabel: this.intl.t(
        'tuition_assistance.applications.manage.tasks.pending_capture_label'
      ),
    }));
  }

  /** Formats uploaded/verified receipt rows; CourseCashBody supplies the View action. */
  get receiptStatusRows(): CourseCashSwipeRow[] {
    const ns = 'tuition_assistance.applications.manage.tasks';
    return (this.args.resolvedReceiptTransactions ?? []).map((transaction) => {
      const verified = transaction.receiptStatus === 'verified';
      return {
        transaction,
        amountLabel: formatCurrency(transaction.amount / 100),
        dateLabel: fullDateLabel(transaction.transactionDate),
        merchantLabel: this.merchantLabelForTransaction(transaction),
        statusLabel: this.intl.t(
          verified ? `${ns}.receipt_verified_label` : `${ns}.receipt_in_review_label`
        ),
        chipIntent: verified ? ('success' as const) : ('warning' as const),
      };
    });
  }

  /** Keep spent/remaining amounts and card details reachable after the use task completes. */
  get completedCourseCashTask(): AttentionTask | undefined {
    return this.args.model.tasks.find(
      (task) => task.kind === 'use-course-cash' && task.status === 'completed'
    );
  }

  /** Styled title for the completed-course-cash drawer card. */
  get completedCourseCashTitle(): SafeString {
    return courseCashMark(
      this.intl.t('tuition_assistance.applications.manage.tasks.use_course_cash_title')
    );
  }

  /** Expand only while an ACTIVATED card still has a spendable balance; other completed cards stay collapsed. */
  get completedCourseCashOpen(): boolean {
    const card = this.args.application.pcsCards?.find((c) => c.isActive);
    return this.cardBalanceDollars > 0 && Boolean(card);
  }

  get spentLabel(): string {
    const amounts = this.args.application.amounts as
      { course_cash?: { used?: number } } | undefined;
    return formatCurrency((amounts?.course_cash?.used ?? 0) / 100);
  }

  chipFor = (task: AttentionTask): string | undefined => {
    if (!task.dueDate) {
      return undefined;
    }
    // Parse the calendar date from the YYYY-MM-DD string directly. Going through
    // `new Date(...).getDate()` reads a UTC-parsed date in local time, which can
    // render an off-by-one day in negative-UTC timezones.
    const datePart = task.dueDate.split('T')[0] ?? '';
    const [, month, day] = datePart.split('-');
    if (!month || !day) {
      return undefined;
    }
    const formatted = `${month}/${day}`;
    const key =
      task.kind === 'use-course-cash'
        ? 'tuition_assistance.applications.manage.tasks.use_by'
        : 'tuition_assistance.applications.manage.tasks.due';
    return this.intl.t(key, { date: formatted });
  };

  /** Available CourseCash balance in dollars (the model stores cents). */
  get cardBalanceDollars(): number {
    return (this.args.application.courseCashAvailableBalance ?? 0) / 100;
  }

  get cardBalanceLabel(): string {
    return formatCurrency(this.cardBalanceDollars);
  }

  get institutionName(): string {
    const value = getSingleValueForTasField(
      'SCHOOL_INSTITUTION_NAME',
      this.args.application.tasProgramInstance?.fields
    );
    return typeof value === 'string' ? value : '';
  }

  /** Last 4 digits of the active CourseCash card, when issued. */
  get cardLast4(): string | undefined {
    const cards = this.args.application.pcsCards ?? [];
    const card = cards.find((c) => c.isActive) ?? cards[0];
    return card?.providerData?.last4;
  }

  /** Keep uploaded documents editable throughout course approval, even after the upload task completes. */
  get completedCourseDocumentsTask(): AttentionTask | undefined {
    return this.args.model.tasks.find(
      (task) => task.kind === 'upload-course-documents' && task.status === 'completed'
    );
  }

  /** Plain (non-branded) title for the completed course-documents drawer card. */
  get completedCourseDocumentsTitle(): string {
    return this.intl.t(TASK_TITLE_KEYS['upload-course-documents']);
  }

  <template>
    {{#if @model.visible}}
      <section class="tio-col gap-4 tio-bg-sunken rounded-2xl p-6" ...attributes>
        <div class="tio-row items-baseline gap-2">
          <h3 class="tio-text-lg-semibold tio-fg-primary">
            {{t "tuition_assistance.applications.manage.heading"}}
          </h3>
          {{#if this.countLabel}}
            <span class="tio-text-sm-normal tio-fg-secondary">{{this.countLabel}}</span>
          {{/if}}
        </div>

        {{! key="kind" — anchorRows/taskRows are plain getters that produce a
          fresh array of new object literals on every access (e.g. any
          courseCashTransactions reload dirties attentionModel even when the
          anchor's own kind/content is unchanged). The each-loop's default
          keying is by object identity, so without an explicit key every
          recompute would tear down and remount AnchorMessage/ConfirmationCheckIcon
          — replaying the mount-once bounce-in animation for no visible reason. }}
        {{#each this.anchorRows key="kind" as |anchor|}}
          {{#if (eq anchor.kind "approved")}}
            <div class="tio-confetti-burst-wrap">
              <AnchorMessage
                @title={{anchor.title}}
                @body={{anchor.body}}
                @variant={{anchor.variant}}
              />
              <div aria-hidden="true" class="tio-confetti-overlay">
                {{#each CONFETTI_PIECES}}
                  <span class="tio-confetti-piece"></span>
                {{/each}}
              </div>
            </div>
          {{else}}
            <AnchorMessage
              @title={{anchor.title}}
              @body={{anchor.body}}
              @variant={{anchor.variant}}
            />
          {{/if}}
        {{/each}}

        {{#each this.taskRows key="task.id" as |row|}}
          <TaskCard
            @icon={{row.icon}}
            @iconColor={{row.color}}
            @title={{row.title}}
            @chip={{row.chip}}
            @chipIntent={{row.chipIntent}}
            @description={{row.description}}
            @isOpen={{row.isOpen}}
            @onToggle={{fn this.toggleCard row.task}}
            @progressDone={{row.progressDone}}
            @progressTotal={{row.progressTotal}}
            @remainingSummary={{row.remainingSummary}}
          >
            <:headerActions>
              {{#if (eq row.task.kind "use-course-cash")}}
                <Button
                  @appearance="minimal"
                  @intent="primary"
                  @size="sm"
                  {{on "click" (if @onViewActivity @onViewActivity noop)}}
                  data-test-view-card-activity
                >
                  <PhCreditCard @size={{16}} class="mr-1" />
                  {{t "tuition_assistance.course_cash.card_details_button"}}
                </Button>
              {{/if}}
            </:headerActions>
            <:body>
              {{#if (eq row.task.kind "use-course-cash")}}
                <CourseCashBody
                  @institutionName={{this.institutionName}}
                  @availableBalance={{this.cardBalanceLabel}}
                  @useByDate={{row.useByDateLabel}}
                  @cardBalance={{this.cardBalanceDollars}}
                  @last4={{this.cardLast4}}
                  @pendingCaptureRows={{this.pendingCaptureRows}}
                  @receiptStatusRows={{this.receiptStatusRows}}
                  @onViewReceipt={{@onViewReceipt}}
                />
              {{else if (eq row.task.kind "submit-evidence")}}
                <SubmitEvidenceBody @application={{@application}} @evidence={{@onSubmitEvidence}} />
              {{else if (eq row.task.kind "upload-course-documents")}}
                <CourseDocumentsBody @application={{@application}} />
              {{else}}
                <div class="tio-col gap-4">
                  <p class="tio-text-sm-normal tio-fg-secondary" data-test-upload-receipt-body>
                    {{t
                      "tuition_assistance.applications.manage.tasks.upload_receipt_body"
                      courseCash=(t "common.tas.course_cash.default")
                      htmlSafe=true
                    }}
                  </p>
                  <div
                    class="tio-row flex-wrap items-baseline gap-2"
                    data-test-upload-receipt-details
                  >
                    {{#if row.transactionDateLabel}}
                      <span class="tio-text-sm-normal tio-fg-primary">
                        {{row.transactionDateLabel}}
                      </span>
                    {{/if}}
                    {{#if row.merchantLabel}}
                      <span
                        class="tio-text-sm-normal tio-fg-primary"
                        data-test-upload-receipt-merchant
                      >
                        {{row.merchantLabel}}
                      </span>
                    {{/if}}
                    {{#if row.amountLabel}}
                      <span class="tio-text-sm-normal tio-fg-primary">{{row.amountLabel}}</span>
                    {{/if}}
                  </div>
                  <div>
                    <Button
                      @intent="primary"
                      data-test-upload-receipt-cta
                      {{on "click" (fn (if @onUploadReceipt @onUploadReceipt noop) row.task)}}
                    >
                      <PhUploadSimple @size={{16}} class="mr-2" />
                      {{t "tuition_assistance.applications.manage.tasks.upload_receipt_cta"}}
                    </Button>
                  </div>
                </div>
              {{/if}}
            </:body>
          </TaskCard>
        {{/each}}

        {{#if this.completedCourseCashTask}}
          <TaskCard
            @icon={{PhCreditCard}}
            @iconColor="purple"
            @title={{this.completedCourseCashTitle}}
            @isOpen={{this.completedCourseCashIsOpen}}
            @onToggle={{fn this.toggleCard this.completedCourseCashTask}}
            data-test-course-cash-drawer
          >
            <:headerActions>
              <Button
                @appearance="minimal"
                @intent="primary"
                @size="sm"
                {{on "click" (if @onViewActivity @onViewActivity noop)}}
                data-test-view-card-activity
              >
                <PhCreditCard @size={{16}} class="mr-1" />
                {{t "tuition_assistance.course_cash.card_details_button"}}
              </Button>
            </:headerActions>
            <:body>
              <CourseCashBody
                @used={{true}}
                @institutionName={{this.institutionName}}
                @spentAmount={{this.spentLabel}}
                @remainingAmount={{this.cardBalanceLabel}}
                @cardBalance={{this.cardBalanceDollars}}
                @last4={{this.cardLast4}}
                @pendingCaptureRows={{this.pendingCaptureRows}}
                @receiptStatusRows={{this.receiptStatusRows}}
                @onViewReceipt={{@onViewReceipt}}
              />
            </:body>
          </TaskCard>
        {{/if}}

        {{#if this.completedCourseDocumentsTask}}
          <TaskCard
            @icon={{PhUploadSimple}}
            @iconColor="teal"
            @title={{this.completedCourseDocumentsTitle}}
            @isOpen={{this.completedCourseDocumentsIsOpen}}
            @onToggle={{fn this.toggleCard this.completedCourseDocumentsTask}}
            data-test-course-documents-drawer
          >
            <:body>
              <CourseDocumentsBody @application={{@application}} />
            </:body>
          </TaskCard>
        {{/if}}
      </section>
    {{/if}}
  </template>
}
